import { ConnectionRequestDescriptor } from '../Models/Api/strongbox.models';
import { AppendMetadataToConnectionEndpoint, ConnectUrlParameter } from './ConnectStrongbox';

export type ConnectWindowLoadingContent = {
    heading: string;
    subText: string;
    accentColor?: string;
};

/*
 * The subset of the connect lifecycle the universal-collector connect thunks depend on, so they can be
 * driven by either the popup ConnectWindow (lender "upload files yourself") or a full-page redirect
 * (borrower secure link). navigate() sends the user to the built connect endpoint; close() tears down any
 * transient UI the navigator owns (the popup, or nothing for a redirect).
 */
export interface IConnectNavigator {
    navigate(descriptor: ConnectionRequestDescriptor | undefined, connectParameters?: ConnectUrlParameter[]): boolean;
    close(): void;
}

/*
 * ConnectWindow encapsulates the two-phase lifecycle of the accounting-system OAuth popup.
 *
 * Browsers only allow window.open during transient user activation (a real, trusted user gesture).
 * The V2 data-collection flow needs several async round-trips to build the connect URL, by which
 * point activation is gone and the popup would be blocked. The fix is to OPEN a blank popup
 * synchronously on the click (while activation is live) and NAVIGATE it to the real endpoint once
 * the async work completes — navigation needs no activation.
 *
 * This intentionally does NOT replace LoadConnectWindow in ConnectStrongbox.ts, which other flows
 * still use synchronously (they already hold the descriptor at click time).
 */
export class ConnectWindow implements IConnectNavigator {
    private handle: Window | undefined;

    // Temporary, stable target name used at open time, before we have a descriptor. Stable so a
    // rapid second click reuses the same window instead of orphaning blank popups.
    private static readonly tempTarget = 'sbx_accounting_connect_popup';

    private static features(): string {
        const width = 750;
        const height = 970;

        return `left=${(document.body.clientWidth / 2) - (width / 2)}` +
            `,top=${(document.body.clientHeight / 2) - (height / 2)}` +
            `,width=${width},height=${height}` +
            ',location=yes,toolbar=0,status=0,menubar=0,scrollbars=0';
    }

    /*
     * MUST be called synchronously within a user gesture (e.g. a click handler) so the browser
     * grants transient activation for window.open. Optionally renders a loading indicator so the
     * user sees something is happening instead of an empty white popup while async setup runs.
     *
     * Returns false if the popup was blocked.
     */
    open(loading?: ConnectWindowLoadingContent): boolean {
        this.handle = window.open('about:blank', ConnectWindow.tempTarget, ConnectWindow.features()) || undefined;

        if (!this.handle) {
            return false;
        }

        if (loading) {
            try {
                ConnectWindow.renderLoading(this.handle.document, loading);
            } catch {
                // Rendering the placeholder is best-effort; ignore if the browser disallows it.
            }
        }

        this.handle.focus && this.handle.focus();

        return true;
    }

    /*
     * Renders the placeholder loading UI into the popup using DOM APIs (createElement / textContent /
     * <style>.textContent) rather than document.write or innerHTML.
     *
     * document.write and innerHTML are Trusted Types "injection sinks" — under a
     * `require-trusted-types-for 'script'` CSP they throw unless handed a TrustedHTML object, which
     * would force us to register a Trusted Types policy. createElement / textContent are not sinks,
     * so this approach needs no policy and is safe regardless of the page's CSP. (The about:blank
     * popup inherits the opener's CSP, so this matters even though the opener sets the policy.)
     */
    private static renderLoading(doc: Document, content: ConnectWindowLoadingContent): void {
        const accent = `#${(content.accentColor || '01bbc8').replace('#', '')}`;

        doc.title = content.heading;

        const style = doc.createElement('style');
        // style.textContent is not a Trusted Types sink (that guards <script> text, not styles).
        style.textContent =
            `html,body{height:100%;margin:0}` +
            `body{display:flex;flex-direction:column;align-items:center;justify-content:center;` +
            `font-family:system-ui,-apple-system,'Segoe UI',Roboto,sans-serif;color:#33415c;background:#fff}` +
            `.spinner{width:42px;height:42px;border:4px solid #e3e8ef;border-top-color:${accent};` +
            `border-radius:50%;animation:spin .9s linear infinite}` +
            `.msg{margin-top:18px;font-size:15px}.sub{margin-top:6px;font-size:13px;color:#7a8699}` +
            `@keyframes spin{to{transform:rotate(360deg)}}`;
        doc.head.appendChild(style);

        const spinner = doc.createElement('div');
        spinner.className = 'spinner';
        spinner.setAttribute('role', 'status');
        spinner.setAttribute('aria-label', 'Loading');
        doc.body.appendChild(spinner);

        const heading = doc.createElement('div');
        heading.className = 'msg';
        heading.textContent = content.heading;
        doc.body.appendChild(heading);

        const sub = doc.createElement('div');
        sub.className = 'sub';
        sub.textContent = content.subText;
        doc.body.appendChild(sub);
    }

    /*
     * Navigates the already-open popup to the real connect endpoint. No user activation required.
     *
     * Preserves the EXACT window name that LoadConnectWindow would have set. The name is assigned
     * while the popup is still same-origin (about:blank); window.name survives the subsequent
     * cross-origin navigation, so the popup ends up identical to a direct open.
     *
     * Returns false if the user closed the popup during async setup.
     */
    navigate(
        descriptor: ConnectionRequestDescriptor | undefined,
        connectParameters?: ConnectUrlParameter[]
    ): boolean {
        if (!this.handle || this.handle.closed) {
            return false;
        }

        let endpoint = 'about:blank';

        if (descriptor && descriptor.connectionEndpoint) {
            endpoint = AppendMetadataToConnectionEndpoint(descriptor.connectionEndpoint, connectParameters);
        }

        if (descriptor) {
            // Exact match to LoadConnectWindow's original window name (ConnectStrongbox.ts).
            try {
                this.handle.name = `${descriptor.datasourceNameId}_${descriptor.id}_login_popup`;
            } catch {
                // Setting name is best-effort; ignore if disallowed.
            }
        }

        this.handle.location.href = endpoint;
        this.handle.focus && this.handle.focus();

        return true;
    }

    get window(): Window | undefined {
        return this.handle;
    }

    get isOpen(): boolean {
        return !!this.handle && !this.handle.closed;
    }

    close(): void {
        try {
            this.handle && this.handle.close();
        } catch {
            // Closing is best-effort.
        }
    }
}

/*
 * Full-page-redirect counterpart to ConnectWindow, used by the borrower universal-collector flow.
 *
 * A borrower who opens a secure upload link has no popup and no transient user activation to open one, and
 * the upload IS their whole session — so instead of navigating a child window we replace the current page
 * with the connect endpoint (mirrors V1 BorrowerPortal's window.location.replace). There is nothing to
 * close, so close() is a no-op.
 */
export class RedirectConnectNavigator implements IConnectNavigator {
    navigate(descriptor: ConnectionRequestDescriptor | undefined, connectParameters?: ConnectUrlParameter[]): boolean {
        if (!descriptor || !descriptor.connectionEndpoint) {
            return false;
        }

        const endpoint = AppendMetadataToConnectionEndpoint(descriptor.connectionEndpoint, connectParameters);
        // Replace (not assign) so the borrower can't "Back" into the intermediate portal page.
        window.location.replace(endpoint);
        return true;
    }

    close(): void {
        // Nothing to close for a full-page redirect.
    }
}
